import { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

function typeSubtitle() {
  const el = document.querySelector('[data-typer]');
  if (!el) return;
  const full = el.getAttribute('data-text') || el.textContent;
  el.textContent = '';
  const o = { i: 0 };
  gsap.to(o, {
    i: full.length,
    duration: Math.min(2.6, full.length * 0.045),
    ease: 'none',
    onUpdate: () => {
      el.textContent = full.slice(0, Math.round(o.i));
    },
  });
}

function initTilt() {
  if (window.matchMedia('(hover: none)').matches) return () => {};
  const cleanups = [];
  document.querySelectorAll('[data-tilt]').forEach((card) => {
    card.style.willChange = 'transform';
    const move = (e) => {
      const r = card.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      gsap.to(card, {
        rotationY: px * 9,
        rotationX: -py * 9,
        y: -6,
        transformPerspective: 900,
        duration: 0.45,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    };
    const leave = () => gsap.to(card, { rotationY: 0, rotationX: 0, y: 0, duration: 0.6, ease: 'power3.out', overwrite: 'auto' });
    card.addEventListener('mousemove', move);
    card.addEventListener('mouseleave', leave);
    cleanups.push(() => {
      card.removeEventListener('mousemove', move);
      card.removeEventListener('mouseleave', leave);
    });
  });
  return () => cleanups.forEach((fn) => fn());
}

export default function Motion() {
  useEffect(() => {
    let cleanupTilt = () => {};

    const ctx = gsap.context(() => {
      if (!window.matchMedia('(hover: none)').matches) {
        ScrollSmoother.create({
          wrapper: '#smooth-wrapper',
          content: '#smooth-content',
          smooth: 1.2,
          effects: false,
        });
      }

      gsap.set('.hero-chip', { y: 16 });

      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.from('.hero-char', { yPercent: 118, duration: 1.05, stagger: 0.055, ease: 'expo.out' })
        .to('.hero-kicker', { opacity: 1, x: 0, duration: 0.7 }, 0.25)
        .from('.hero-kicker', { x: -24, duration: 0.7 }, 0.25)
        .add(() => typeSubtitle(), 0.75)
        .to('.hero-cta', { opacity: 1, y: 0, duration: 0.8 }, 1.35)
        .from('.hero-cta', { y: 26, duration: 0.8 }, 1.35)
        .to('.hero-chip', { opacity: 1, y: 0, duration: 0.9, stagger: 0.15 }, 1.7);

      document.querySelectorAll('[data-anim]').forEach((el) => {
        gsap.from(el, {
          y: 44,
          opacity: 0,
          duration: 0.95,
          ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 88%' },
        });
      });

      document.querySelectorAll('[data-anim-line]').forEach((el, i) => {
        gsap.from(el, {
          y: 28,
          opacity: 0,
          duration: 0.9,
          delay: i * 0.12,
          ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 90%' },
        });
      });

      if (document.querySelector('[data-skill]')) {
        gsap.from('[data-skill]', {
          y: 60,
          opacity: 0,
          duration: 0.9,
          stagger: 0.11,
          ease: 'power3.out',
          scrollTrigger: { trigger: '[data-skill]', start: 'top 85%' },
        });
      }

      if (document.querySelector('[data-project]')) {
        gsap.from('[data-project]', {
          y: 70,
          opacity: 0,
          scale: 0.985,
          duration: 1,
          stagger: 0.09,
          ease: 'power3.out',
          scrollTrigger: { trigger: '[data-project]', start: 'top 88%' },
        });
      }

      const line = document.querySelector('[data-timeline-line]');
      if (line) {
        gsap.to(line, {
          scaleY: 1,
          ease: 'none',
          scrollTrigger: { trigger: '[data-timeline]', start: 'top 72%', end: 'bottom 72%', scrub: 0.6 },
        });
      }

      document.querySelectorAll('[data-count]').forEach((el) => {
        const target = parseInt(el.getAttribute('data-count'), 10) || 0;
        const o = { v: 0 };
        el.textContent = '0';
        gsap.to(o, {
          v: target,
          duration: 1.6,
          ease: 'power2.out',
          onUpdate: () => {
            el.textContent = Math.round(o.v);
          },
          scrollTrigger: { trigger: el, start: 'top 92%' },
        });
      });

      cleanupTilt = initTilt();
      ScrollTrigger.refresh();
    });

    return () => {
      cleanupTilt();
      ctx.revert();
    };
  }, []);

  return null;
}
